/**
 * Editor de líneas de un documento de venta.
 *
 * Lo que hace especial a este componente no se ve: **los totales los
 * calcula `computeDocumentTotals` del dominio**, la misma función que usa
 * el servidor al guardar.
 *
 * No es purismo. El IVA de un documento se calcula sobre la **base
 * acumulada por tipo**, nunca sumando el IVA de cada línea: con veinte
 * líneas al 21 %, las dos formas dan resultados distintos por redondeo, y
 * la diferencia acaba en un registro Veri\*Factu que ya no se puede
 * cambiar. Si la pantalla sumara por su cuenta, enseñaría un total y el
 * servidor guardaría otro.
 *
 * Por eso el dinero nunca pasa por `number`: entra como cadena decimal,
 * se convierte a `Money` y vuelve a salir como cadena.
 */

import {
  KINDS,
  TAX_RATES,
  emptyLine,
  type EditableLine,
  type LineKind,
  type LineTotals,
} from './line-totals.js';

export {
  KINDS,
  TAX_RATES,
  computeLineTotals,
  emptyLine,
  useLineTotals,
  type EditableLine,
  type LineKind,
  type LineTotals,
} from './line-totals.js';

interface Props {
  lines: EditableLine[];
  onChange: (lines: EditableLine[]) => void;
  totals: LineTotals;
  /** Se usa para generar identificadores locales estables. */
  nextId: () => string;
  disabled?: boolean;
}

export function LineEditor({ lines, onChange, totals, nextId, disabled }: Props): React.ReactNode {
  function update(lineId: string, patch: Partial<EditableLine>): void {
    onChange(lines.map((line) => (line.lineId === lineId ? { ...line, ...patch } : line)));
  }

  function remove(lineId: string): void {
    onChange(lines.filter((line) => line.lineId !== lineId));
  }

  function move(index: number, delta: number): void {
    const target = index + delta;
    if (target < 0 || target >= lines.length) return;
    const next = [...lines];
    const [moved] = next.splice(index, 1);
    if (moved) next.splice(target, 0, moved);
    onChange(next);
  }

  return (
    <div className="lines">
      <div className="scroller">
        <table className="table__grid table__grid--compact lines__table">
          <thead>
            <tr>
              <th>Concepto</th>
              <th style={{ width: '9rem' }}>Tipo</th>
              <th className="table__num" style={{ width: '6rem' }}>Cant.</th>
              <th className="table__num" style={{ width: '8rem' }}>Precio</th>
              <th className="table__num" style={{ width: '6rem' }}>Dto. %</th>
              <th className="table__num" style={{ width: '6rem' }}>IVA %</th>
              <th className="table__num" style={{ width: '8rem' }}>Base</th>
              <th style={{ width: '6rem' }} />
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => {
              const invalid = totals.invalidLines.includes(line.lineId);
              return (
                <tr key={line.lineId} className={invalid ? 'lines__row--invalid' : undefined}>
                  <td>
                    <input
                      className="lines__input"
                      value={line.description}
                      disabled={disabled ?? false}
                      aria-label={`Concepto de la línea ${index + 1}`}
                      onChange={(e) => update(line.lineId, { description: e.target.value })}
                    />
                  </td>
                  <td>
                    <select
                      className="lines__input"
                      value={line.kind}
                      disabled={disabled ?? false}
                      aria-label={`Tipo de la línea ${index + 1}`}
                      onChange={(e) => update(line.lineId, { kind: e.target.value as LineKind })}
                    >
                      {KINDS.map((kind) => (
                        <option key={kind.value} value={kind.value}>
                          {kind.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      className="lines__input table__num"
                      inputMode="decimal"
                      value={line.quantity}
                      disabled={(disabled ?? false) || line.kind === 'TEXTO'}
                      aria-label={`Cantidad de la línea ${index + 1}`}
                      onChange={(e) => update(line.lineId, { quantity: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      className="lines__input table__num"
                      inputMode="decimal"
                      value={line.unitPrice}
                      disabled={(disabled ?? false) || line.kind === 'TEXTO'}
                      aria-label={`Precio de la línea ${index + 1}`}
                      onChange={(e) => update(line.lineId, { unitPrice: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      className="lines__input table__num"
                      inputMode="decimal"
                      value={line.discountRate}
                      disabled={(disabled ?? false) || line.kind === 'TEXTO'}
                      aria-label={`Descuento de la línea ${index + 1}`}
                      onChange={(e) => update(line.lineId, { discountRate: e.target.value })}
                    />
                  </td>
                  <td>
                    <select
                      className="lines__input table__num"
                      value={line.taxRate}
                      disabled={(disabled ?? false) || line.kind === 'TEXTO'}
                      aria-label={`IVA de la línea ${index + 1}`}
                      onChange={(e) => update(line.lineId, { taxRate: e.target.value })}
                    >
                      {TAX_RATES.map((rate) => (
                        <option key={rate} value={rate}>
                          {rate}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="table__num">
                    {line.kind === 'TEXTO' ? (
                      <span className="due--none">—</span>
                    ) : invalid ? (
                      <span className="due due--late">?</span>
                    ) : (
                      (totals.perLine[line.lineId] ?? '0.00')
                    )}
                  </td>
                  <td>
                    <div className="lines__actions">
                      <button
                        type="button"
                        className="lines__button"
                        aria-label={`Subir la línea ${index + 1}`}
                        disabled={disabled ?? false}
                        onClick={() => move(index, -1)}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="lines__button"
                        aria-label={`Bajar la línea ${index + 1}`}
                        disabled={disabled ?? false}
                        onClick={() => move(index, 1)}
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="lines__button lines__button--danger"
                        aria-label={`Quitar la línea ${index + 1}`}
                        disabled={(disabled ?? false) || lines.length === 1}
                        onClick={() => remove(line.lineId)}
                      >
                        ✕
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="lines__foot">
        <button
          type="button"
          className="button button--quiet button--small"
          disabled={disabled ?? false}
          onClick={() => onChange([...lines, emptyLine(nextId())])}
        >
          Añadir línea
        </button>

        {totals.invalidLines.length > 0 && (
          <span className="lines__warn">
            ⚠ {totals.invalidLines.length}{' '}
            {totals.invalidLines.length === 1 ? 'línea con números' : 'líneas con números'} que no se
            pueden interpretar: no cuentan en el total.
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Resumen de totales, con el desglose por tipo de IVA.
 *
 * El desglose no es decorativo: el art. 6.1.g del RD 1619/2012 obliga a
 * que la factura lo lleve, y es lo que después viaja al registro
 * Veri\*Factu.
 */
export function TotalsSummary({
  totals,
  currency = 'EUR',
}: {
  totals: LineTotals;
  currency?: string;
}): React.ReactNode {
  const symbol = currency === 'EUR' ? '€' : currency;

  return (
    <div className="totals">
      <dl className="totals__list">
        <div>
          <dt>Bruto</dt>
          <dd>
            {totals.gross} {symbol}
          </dd>
        </div>
        {totals.discount !== '0.00' && (
          <div>
            <dt>Descuento global</dt>
            <dd>
              −{totals.discount} {symbol}
            </dd>
          </div>
        )}
        <div>
          <dt>Base imponible</dt>
          <dd>
            {totals.taxableBase} {symbol}
          </dd>
        </div>

        {totals.breakdown.map((entry) => (
          <div key={entry.taxRate}>
            <dt>
              IVA {entry.taxRate} % sobre {entry.taxableBase} {symbol}
            </dt>
            <dd>
              {entry.taxAmount} {symbol}
            </dd>
          </div>
        ))}

        <div className="totals__grand">
          <dt>Total</dt>
          <dd>
            {totals.total} {symbol}
          </dd>
        </div>
      </dl>
    </div>
  );
}
